import { addError, logger } from 'monitor';
import { useCallback, useMemo, useState } from 'react';

import { useBulkDeleteEvidence, useOfflineEvidenceStore } from '../../../evidence';
import { useNetworkStatus } from '../../../network-status';
import { isNewEvidence, isOfflineEvidence, isOnlineEvidence, NewEvidence } from '../../types';
import { compressImage } from '../../utils/compressImage';
import { createNewEvidence } from '../../utils/createBaleFromFile';
import { validateFile } from '../../utils/files/validateFile/validateFile';
import { trackEvidenceSopOutcome } from '../../utils/trackBaleSopOutcome';
import { useEvidenceAnalysisStore } from '../useBaleAnalysis/useBaleAnalysisStore';
import { useGetEvidence } from '../useGetEvidence/useGetEvidence';
import { useUploadMaterialEvidence } from '../useUploadBale/useUploadBale';

export const useEvidenceAnalysis = () => {
  const { isOnline } = useNetworkStatus();
  const { mutateAsync: uploadEvidence } = useUploadMaterialEvidence();
  const {
    evidence: newEvidence,
    parentId,
    mode,
    sopMode,
    actions: { addEvidence, removeEvidence, updateEvidence },
  } = useEvidenceAnalysisStore();
  const {
    removeEvidenceFile: removeOfflineEvidence,
    addEvidenceFile: cacheEvidenceOffline,
    getPhotoFromOfflineFile,
  } = useOfflineEvidenceStore();
  const { deleteEvidenceAsync: removeOnlineEvidence, isPending: isRemovePending } = useBulkDeleteEvidence({
    mode,
    onError: addError,
    parentId,
  });

  const [isSyncingOfflineEvidence, setIsSyncingOfflineEvidence] = useState(false);
  const [isClearingOfflineEvidence, setIsClearingOfflineEvidence] = useState(false);

  const {
    evidence: materialEvidenceList,
    isLoading: isEvidenceLoading,
    refetchEvidence,
  } = useGetEvidence(mode, parentId);
  const isLoading =
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    useMemo(() => newEvidence.some((evidence) => evidence.isLoading), [newEvidence]) || isEvidenceLoading;

  const isPhotoRejected = useMemo(
    () =>
      materialEvidenceList
        .filter((evidence) => isNewEvidence(evidence) || isOnlineEvidence(evidence))
        .some((evidence) => evidence.analysisStatus === 'rejected-photo'),
    [materialEvidenceList]
  );
  const isMaterialRejected = useMemo(
    () =>
      materialEvidenceList
        .filter(isOnlineEvidence)
        .some((evidence) => evidence.analysisStatus === 'rejected-material' && evidence.loadingStatus === undefined),
    [materialEvidenceList]
  );

  const validateEvidence = useCallback(
    async (evidence: NewEvidence): Promise<NewEvidence> => {
      const existingFiles = newEvidence.filter(isNewEvidence).map((b) => b.file);
      const validation = await validateFile(evidence.file, {
        existingFiles,
        logContext: { evidenceId: evidence.id },
        sopMode,
      });

      if (!validation.isValid) {
        return {
          ...evidence,
          analysisStatus: 'rejected-photo',
          photoRejectedReason: validation.rejectionReason,
        };
      }

      return evidence;
    },
    [newEvidence, sopMode]
  );

  const cacheEvidence = useCallback(
    (evidence: NewEvidence, model?: string) => {
      cacheEvidenceOffline(
        {
          model: model,
          parentId,
          photo: evidence.file,
          startTime: Date.now(),
        },
        { fileId: evidence.id, mode }
      );
      updateEvidence({
        ...evidence,
        variant: 'offline' as const,
      });
    },
    [cacheEvidenceOffline, updateEvidence, parentId, mode]
  );

  const add = useCallback(
    async ({ photo, model }: { photo: File; model?: string }) => {
      logger.info('Adding photo evidence', { photo });
      const processStartTime = Date.now();

      const evidence = await validateEvidence(createNewEvidence(photo));
      const isClientSopFailed = evidence.analysisStatus === 'rejected-photo';

      if (isClientSopFailed) {
        logger.info('Client-side SOP failed', { bale: evidence, photoRejectedReason: evidence.photoRejectedReason });
        trackEvidenceSopOutcome(evidence, 'frontend');
        addEvidence(evidence);
        return;
      }

      try {
        logger.info('Compressing bale photo', { photo });
        const compressedPhoto = await compressImage(photo);
        logger.info('Bale photo compressed', {
          compressedSize: compressedPhoto.size,
          originalSize: photo.size,
          percentage: Math.round((compressedPhoto.size / photo.size) * 100),
        });
        evidence.file = compressedPhoto;

        logger.info('Caching bale photo', { photo });

        logger.info('Uploading bale photo', { photo });
        if (isOnline) {
          await uploadEvidence({ evidence: evidence, model, startTime: processStartTime }); // adds to online evidence
          await removeOfflineEvidence(evidence.id); // removes from offline evidence
        } else {
          cacheEvidence(evidence);
        }
      } catch (error) {
        addError(error);
        cacheEvidence(evidence);
      }
    },
    [validateEvidence, addEvidence, isOnline, uploadEvidence, removeOfflineEvidence, cacheEvidence]
  );

  const retryAnalysis = useCallback(
    ({ evidenceId, model }: { evidenceId: string; model?: string }) => {
      const evidence = materialEvidenceList.filter(isNewEvidence).find((evidence) => evidence.id === evidenceId);
      if (!evidence) {
        addError(new Error('Evidence not found; unable to retry;'));
        return;
      }

      uploadEvidence({ evidence: evidence, model, startTime: Date.now() });
    },
    [materialEvidenceList, uploadEvidence]
  );

  const remove = useCallback(
    async (evidenceId: string, onSuccess?: () => void) => {
      const evidence = materialEvidenceList.find((evidence) => evidence.id === evidenceId);
      if (!evidence) {
        logger.error('Evidence not found', { evidenceId }, Error('Evidence not found'));
        return;
      }
      logger.info(`Removing local evidence ${evidenceId}`);
      removeEvidence(evidenceId);

      logger.info(`Removing offline evidence ${evidenceId}`);
      await removeOfflineEvidence(evidence.id);

      if (!isOnlineEvidence(evidence)) {
        logger.info('Evidence not analyzed, skipping online delete', { evidence });
        return;
      }
      logger.info(`Deleting online evidence ${evidence.evidenceId}`, {
        evidence,
      });
      const deleted = await removeOnlineEvidence([evidence.evidenceId]);
      if (deleted) {
        onSuccess?.();
        await refetchEvidence();
      }
    },
    [materialEvidenceList, refetchEvidence, removeEvidence, removeOfflineEvidence, removeOnlineEvidence]
  );

  const syncOfflineEvidence = useCallback(async () => {
    if (!isOnline) {
      logger.warn('Cannot sync offline evidence while offline');
      return;
    }
    setIsSyncingOfflineEvidence(true);

    const offlineEvidence = materialEvidenceList.filter(isOfflineEvidence);
    await Promise.all(
      offlineEvidence.map(async (evidence) => {
        const file = await getPhotoFromOfflineFile(evidence.id);
        if (!file) {
          throw new Error(`Failed to retrieve photo for offline file ${evidence.id}`);
        }

        try {
          await uploadEvidence({
            evidence: {
              ...evidence,
              file: file,
              variant: 'new' as const,
            },
            startTime: Date.now(),
          });
          await removeOfflineEvidence(evidence.id);
        } catch (error) {
          addError(error);
        }
      })
    );
    setIsSyncingOfflineEvidence(false);
  }, [isOnline, materialEvidenceList, getPhotoFromOfflineFile, uploadEvidence, removeOfflineEvidence]);

  const clearOfflineEvidence = useCallback(async () => {
    setIsClearingOfflineEvidence(true);
    const offlineEvidence = materialEvidenceList.filter(isOfflineEvidence);
    await Promise.all(offlineEvidence.map(async (evidence) => removeOfflineEvidence(evidence.id)));
    setIsClearingOfflineEvidence(false);
  }, [materialEvidenceList, removeOfflineEvidence]);

  const offlineEvidenceList = useMemo(() => materialEvidenceList.filter(isOfflineEvidence), [materialEvidenceList]);
  return {
    addEvidence: add,
    clearOfflineEvidence,
    isClearingOfflineEvidence,
    isEvidenceLoading,
    isLoading,
    isMaterialRejected,
    isPhotoRejected,
    isRemovePending,
    isSyncingOfflineEvidence,
    materialEvidenceList,
    offlineEvidenceList,
    removeEvidence: remove,
    retryAnalysis,
    syncOfflineEvidence,
    updateEvidence,
  };
};
